import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import {
  AccountingPeriodSchema,
  AssetSchema,
  ClientSchema,
  EmployeeSchema,
  ProjectSchema,
  SupplierSchema,
} from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { getData } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ActiveClientPicker, ProjectSearchPicker } from '../crm/common';
import { EmployeePicker, MasterSelect, SupplierPicker, useActiveMasters } from '../finance/lookups';
import { AssetPicker } from '../procurement/purchasing';

/**
 * What every financial report shares: its filters held in the URL, so a report survives a refresh,
 * can be bookmarked, and a figure can link to the report or list behind it with the same filters
 * (FRD-RPT-007, FRD-FIN-137); and the dimension combination (FRD-FIN-053, FIN-129).
 */

export const DIMENSION_KEYS = [
  'costCenterId',
  'locationId',
  'projectId',
  'clientId',
  'supplierId',
  'employeeId',
  'assetId',
] as const;

/** The report's URL state and the API query for it. */
export function useReportParams() {
  const [params, setParams] = useSearchParams();
  const get = (key: string) => params.get(key) ?? '';
  const set = (values: Readonly<Record<string, string>>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };
  /** The query string for the API: the named keys, and every dimension that is set. */
  const query = (keys: readonly string[]) => {
    const out = new URLSearchParams();
    for (const key of [...keys, ...DIMENSION_KEYS]) {
      const value = get(key).trim();
      if (value !== '') out.set(key, value);
    }
    return out;
  };
  return { get, set, query };
}

/** The first and last day of the current business year, as a report's default range. */
export function yearToDate(today: string): { from: string; to: string } {
  return { from: `${today.slice(0, 4)}-01-01`, to: today };
}

/** A date range with a quick pick of an accounting period (FRD-FIN-108). */
export function RangeFilter({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) {
  const session = useSession();
  const periods = useQuery({
    queryKey: ['accounting-periods'],
    enabled: hasCapability(session.data, 'ledger.accounting_period:view'),
    queryFn: () => getData('/api/v1/ledger/accounting-periods', z.array(AccountingPeriodSchema)),
  });
  const matching = periods.data?.find((p) => p.startDate === from && p.endDate === to);
  return (
    <>
      {periods.data !== undefined && periods.data.length > 0 && (
        <TextField
          select
          size="small"
          label="Accounting period"
          value={matching?.id ?? ''}
          sx={{ minWidth: 180 }}
          onChange={(e) => {
            const period = periods.data.find((p) => p.id === e.target.value);
            if (period !== undefined) onChange({ from: period.startDate, to: period.endDate });
          }}
        >
          <MenuItem value="">Custom dates</MenuItem>
          {periods.data.map((period) => (
            <MenuItem key={period.id} value={period.id}>
              {period.code}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        size="small"
        type="date"
        label="From"
        value={from}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          onChange({ from: e.target.value, to });
        }}
      />
      <TextField
        size="small"
        type="date"
        label="To"
        value={to}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          onChange({ from, to: e.target.value });
        }}
      />
    </>
  );
}

/** One record read by id for a picker's value, where the session may read it. */
function useOne<T>(path: string, id: string, capability: string, schema: z.ZodType<T>) {
  const session = useSession();
  return useQuery({
    queryKey: ['report-dimension', path, id],
    enabled: id !== '' && hasCapability(session.data, capability),
    retry: false,
    queryFn: () => getData(`${path}/${id}`, schema),
  });
}

/**
 * The dimension combination (FRD-FIN-053, FIN-129): each dimension from its real master, offered
 * only where the session may read that master.
 */
export function DimensionFilter() {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const report = useReportParams();
  const costCentres = useActiveMasters(
    '/api/v1/organization/cost-centers',
    can('organization.cost_center:view'),
  );
  const locations = useActiveMasters(
    '/api/v1/organization/locations',
    can('organization.location:view'),
  );
  const project = useOne(
    '/api/v1/projects',
    report.get('projectId'),
    'projects.project:view',
    ProjectSchema,
  );
  const client = useOne(
    '/api/v1/crm/clients',
    report.get('clientId'),
    'crm.client:view',
    ClientSchema,
  );
  const supplier = useOne(
    '/api/v1/procurement/suppliers',
    report.get('supplierId'),
    'procurement.supplier:view',
    SupplierSchema,
  );
  const employee = useOne(
    '/api/v1/hr/employees',
    report.get('employeeId'),
    'hr.employee:view',
    EmployeeSchema,
  );
  const asset = useOne(
    '/api/v1/assets/assets',
    report.get('assetId'),
    'assets.asset:view',
    AssetSchema,
  );
  const box = (child: ReactNode) => <Box sx={{ minWidth: 220 }}>{child}</Box>;
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      useFlexGap
      sx={{ flexWrap: 'wrap' }}
      role="group"
      aria-label="Dimensions"
    >
      {can('organization.cost_center:view') && (
        <MasterSelect
          label="Cost centre"
          value={report.get('costCenterId')}
          options={costCentres.data?.items ?? []}
          onChange={(value) => {
            report.set({ costCenterId: value });
          }}
        />
      )}
      {can('organization.location:view') && (
        <MasterSelect
          label="Location"
          value={report.get('locationId')}
          options={locations.data?.items ?? []}
          onChange={(value) => {
            report.set({ locationId: value });
          }}
        />
      )}
      {can('projects.project:view') &&
        box(
          <ProjectSearchPicker
            label="Project"
            value={project.data ?? null}
            onChange={(value) => {
              report.set({ projectId: value?.id ?? '' });
            }}
          />,
        )}
      {can('crm.client:view') &&
        box(
          <ActiveClientPicker
            label="Client"
            value={client.data ?? null}
            onChange={(value) => {
              report.set({ clientId: value?.id ?? '' });
            }}
          />,
        )}
      {can('procurement.supplier:view') &&
        box(
          <SupplierPicker
            required={false}
            value={supplier.data ?? null}
            onChange={(value) => {
              report.set({ supplierId: value?.id ?? '' });
            }}
          />,
        )}
      {can('hr.employee:view') &&
        box(
          <EmployeePicker
            value={employee.data ?? null}
            onChange={(value) => {
              report.set({ employeeId: value?.id ?? '' });
            }}
          />,
        )}
      {can('assets.asset:view') &&
        box(
          <AssetPicker
            value={asset.data ?? null}
            onChange={(value) => {
              report.set({ assetId: value?.id ?? '' });
            }}
          />,
        )}
    </Stack>
  );
}

/** A right-aligned money cell; an empty or zero amount is left blank to keep columns readable. */
export function Money({ value, bold = false }: { value: string; bold?: boolean }) {
  return (
    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: bold ? 700 : undefined }}>
      {value === '0.00' || value === '' ? '' : value}
    </TableCell>
  );
}
